import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutShippingPageContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { isUpdatingCart } = state.pages.checkoutShipping;
    return {
        isContinueButtonDisabled: !cartState.value || cartState.isLoading || isUpdatingCart,
        pageTitle: getCurrentPage(state).fields.title,
        isButtonWrapperEnable: state.pages.checkoutShipping,
        currentUserIsGuest: getCurrentUserIsGuest(state),
    };
};

type Props = ReturnType<typeof mapStateToProps> & WidgetProps;

export interface CheckoutShippingHeaderStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    buttonsWrapper?: InjectableCss;
    continueButton?: ButtonPresentationProps;
    returnToCartWrapper?: InjectableCss;
}

export const pageHeaderStyles: CheckoutShippingHeaderStyles = {
    container: {
        css: css`
            > div[class^="GridOffset"] {
                margin: 0;
                width: 100%;
            }
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            padding: 0;
            justify-content: flex-end;
            > div {
                margin-top: 30px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            justify-content: flex-start;
                            width: 100%;
                        `,
                        css`
                            justify-content: flex-start;
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    heading: { variant: "h2" },
    buttonsWrapper: {
        css: css`
            margin-left: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 49%;
                        `,
                        css`
                            width: 49%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    returnToCartWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 49%;
                            justify-content: flex-start;
                            padding-left: 0;
                            > div[class^="Wrapper"] {
                                width: 100%;
                                button {
                                    width: 100%;
                                }
                            }
                        `,
                        css`
                            width: 49%;
                            justify-content: flex-start;
                            padding-left: 0;
                            > div[class^="Wrapper"] {
                                width: 100%;
                                button {
                                    width: 100%;
                                }
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },
    continueButton: {
        css: css`
            min-height: 40px;
            width: 147px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = pageHeaderStyles;

const CheckoutShippingFooterCustom: FC<Props> = ({
    isContinueButtonDisabled,
    id,
    isButtonWrapperEnable,
    currentUserIsGuest,
}: Props) => {
    return (
        <GridContainer {...styles.container} className="checkout-custom-footer">
            {isButtonWrapperEnable && !currentUserIsGuest && (
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.returnToCartWrapper}>
                        <Zone contentId={id} zoneName="Content" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button
                            type="submit"
                            disabled={isContinueButtonDisabled}
                            {...styles.continueButton}
                            data-test-selector="checkoutShipping_continue"
                        >
                            {translate("Continue")}
                        </Button>
                    </StyledWrapper>
                </GridItem>
            )}
            {currentUserIsGuest && (
                <GridItem {...styles.gridItem}>
                    <StyledWrapper {...styles.returnToCartWrapper}>
                        <Zone contentId={id} zoneName="Content" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button
                            type="submit"
                            disabled={isContinueButtonDisabled}
                            {...styles.continueButton}
                            data-test-selector="checkoutShipping_continue"
                        >
                            {translate("Continue")}
                        </Button>
                    </StyledWrapper>
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CheckoutShippingFooterCustom),
    definition: {
        displayName: "Checkout Shipping Footer",
        group: "Checkout - Shipping",
        allowedContexts: [CheckoutShippingPageContext],
    },
};

export default widgetModule;
